import { useState } from 'react';
import { cn } from '@shared/lib/cn';
import { hashString, initialsOf } from '@shared/lib/initials';

const SIZES = {
  xs: 'size-6 text-[0.6rem]',
  sm: 'size-8 text-xs',
  md: 'size-10 text-sm',
  lg: 'size-14 text-lg',
  xl: 'size-20 text-2xl',
} as const;

// Six theme-aware muted pairs derived from primary / gold / info / success / warning / neutral.
const PALETTE = [
  'bg-primary-subtle text-on-primary-subtle',
  'bg-accent-subtle text-accent-text',
  'bg-info-subtle text-info-text',
  'bg-success-subtle text-success-text',
  'bg-warning-subtle text-warning-text',
  'bg-surface-sunken text-secondary',
] as const;

export interface AvatarProps {
  name: string;
  src?: string | null;
  size?: keyof typeof SIZES;
  /** Decorative when the name is shown next to it (default). */
  decorative?: boolean;
  className?: string;
}

export function Avatar({ name, src, size = 'md', decorative = true, className }: AvatarProps) {
  const [failed, setFailed] = useState(false);
  const palette = PALETTE[hashString(name) % PALETTE.length];
  const a11y = decorative ? { 'aria-hidden': true as const } : { role: 'img' as const, 'aria-label': name };
  if (src && !failed) {
    return (
      <img
        src={src}
        alt={decorative ? '' : name}
        onError={() => setFailed(true)}
        className={cn('shrink-0 rounded-full object-cover', SIZES[size], className)}
      />
    );
  }
  return (
    <span
      {...a11y}
      className={cn(
        'inline-flex shrink-0 select-none items-center justify-center rounded-full font-medium leading-none',
        SIZES[size],
        palette,
        className,
      )}
    >
      <bdi>{initialsOf(name)}</bdi>
    </span>
  );
}
